import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import { isAxiosError } from 'axios';

interface UpdatePartnershipAdTagPayload {
  partnershipId: number | string;
  tag: string;
}

interface UpdatePartnershipAdTagResponse {
  success: boolean;
}

interface ErrorResponse {
  message: string;
  details?: string;
}

export const updatePartnershipAdTag = async (
  {
    partnershipId,
    tag,
  }: UpdatePartnershipAdTagPayload,
): Promise<UpdatePartnershipAdTagResponse> => {
  const res = await apiClient.patch<UpdatePartnershipAdTagResponse>(
    `${queryKeys.partnerships}/${partnershipId}`,
    { tag },
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to update partnership ad tag');
  }

  return res.data;
};

type MutationFnType = (
  data: UpdatePartnershipAdTagPayload,
) => ReturnType<typeof updatePartnershipAdTag>;

interface UseUpdatePartnershipAdTagOptions {
  config?: MutationConfig<MutationFnType>;
}

export const useUpdatePartnershipAdTag = ({
  config,
}: UseUpdatePartnershipAdTagOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.partnerships],
      });

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: updatePartnershipAdTag,
  });
};
